import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAccount } from '../../contexts/Account';
import { searchCompanies } from '../../services/advisor_services';
import BoardCard from '../BoardCard';
import BoardSkeleton from '../../components/SkeletonLoading/boardSkeleton';
import eventBus, { eventBusValues } from '../../eventBus';

const companyPrimaryExpertiseOptions = [
  { value: 'Founder or CEO', label: 'Founder or CEO' },
  { value: 'Marketing', label: 'Marketing' },
  // { value: 'Business Professional', label: 'Business Professional' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Product', label: 'Product' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Human Resources', label: 'Human Resources' },
  { value: 'Legal', label: 'Legal' },
  // { value: 'Other', label: 'Other' },
];

const sanitizeLogo = (company = {}) => {
  if (company.companyLogo) return company.companyLogo;
  if (company.image?.location) {
    return company.image.location.replace(/^https?:\/\//, '');
  }
  return null;
};

const ExpertiseBoards = () => {
  const { accountDetails } = useAccount();
  const [expertise, setExpertise] = useState('');
  const [companies, setCompanies] = useState([]);
  const [hideScroll, setHideScroll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const shouldMeasure = isLoading || companies.length > 0;

  useEffect(() => {
    if (!shouldMeasure) return undefined;

    const element = containerRef.current;
    if (!element) return undefined;

    const compute = () => {
      const rect = element.getBoundingClientRect();
      const measured = rect?.width || element.offsetWidth || 0;
      const viewportWidth = Math.max(0, (window.innerWidth || 0) - 80);
      const next = measured > 0 ? Math.min(measured, viewportWidth) : viewportWidth;
      setWidth(next);
    };

    // Initial measure after paint
    requestAnimationFrame(compute);
    window.addEventListener('resize', compute);

    const observer = new ResizeObserver(() => requestAnimationFrame(compute));
    observer.observe(element);

    return () => {
      window.removeEventListener('resize', compute);
      observer.disconnect();
    };
  }, [shouldMeasure]);

  useEffect(() => {
    const primary = accountDetails?.aobfPrimaryExpertise;
    if (primary && companyPrimaryExpertiseOptions.find((o) => o.value === primary)) {
      setExpertise(primary);
      return;
    }
    const random =
      companyPrimaryExpertiseOptions[
        Math.floor(Math.random() * companyPrimaryExpertiseOptions.length)
      ].value;
    setExpertise(random);
  }, [accountDetails?.aobfPrimaryExpertise]);

  useEffect(() => {
    if (!accountDetails?.id || !expertise) return;
    setIsLoading(true);
    searchCompanies({
      ADVISOR_ID: accountDetails.id,
      PEER_FILTER: 'Company',
      EXPERTISE: expertise,
      OFFSET: 0,
    })
      .then((response) => {
        const list = response?.data?.searchCompanies || [];
        const valid = list
          .filter(
            (c) =>
              c.boardOwnerStatus !== 'inactive' &&
              c.boardOwnerStatus !== 'paused' &&
              !['dormant', 'pending_cancellation', 'position_filled'].includes(
                c.opportunityStage
              )
          )
          .map((c) => ({ ...c, companyLogo: sanitizeLogo(c) }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setCompanies(valid);
      })
      .catch((e) => console.error('Error searching companies by expertise:', e))
      .finally(() => setIsLoading(false));
  }, [accountDetails?.id, expertise]);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollWidth } = container;
    if (scrollWidth <= width) setHideScroll(true);
    else setHideScroll(false);
  }, [width, companies, isXs, isLoading]);

  const total = companies.length;
  let displayed = companies;
  // if (isXs && !expanded && total > 2) displayed = companies.slice(0, 2);

  if (!isLoading && total === 0) return null;

  const showMoreButton = isXs && total > 2;

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const jump = 300;
    if (scrollLeft + clientWidth + 50 >= scrollWidth) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: jump, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const jump = 300;
    if (container.scrollLeft === 0) {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -jump, behavior: 'smooth' });
    }
  };

  const handleCardClick = (company) => {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
      companyId: company.id,
    });
  };

  return (
    <Paper
      id="expertise-boards-tile"
      ref={containerRef}
      elevation={1}
      sx={{
        padding: '15px',
        paddingRight: { xxs: '26px', xs: '15px' },
        borderRadius: { xxs: '0', xs: '12px' },
        marginBottom: '20px',
        boxShadow: 'none',
        width: { xxs: '110%', xs: '100%' },
        marginLeft: { xxs: '-10px', xs: '0px' },
        position: 'relative',
        border: '1px solid #E0E0E0',
        display: isLoading || total > 0 ? 'block' : 'none',
      }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: '20px', mb: '4px', color: '#001030' }}
      >
        {`🔎 Boards Seeking ${expertise} Advice`}
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 14,
          color: '#4D586E',
          fontStyle: 'italic',
          mb: '16px',
        }}
      >
        {`Your unique skill set could be a perfect fit for some of these boards currently seeking ${expertise} help and outside perspective.`}
      </Typography>

      {/* {!hideScroll && displayed.length > 1 && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            left: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )} */}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'visible',
          whiteSpace: 'nowrap',
          scrollBehavior: 'smooth',
          overscrollBehaviorX: 'contain',
          marginRight: { xxs: 0, xs: hideScroll ? 0 : '40px' },
          maxWidth: {
            xxs: '100%',
            xs: width - 40,
            md: width + 200,
            lg: width + 500,
          },
        }}
      >
        {isLoading
          ? Array.from({ length: isXs ? 1 : 3 }).map((_, index) => (
              <Box
                key={`expertise-skeleton-${index}`}
                sx={{
                  marginBottom: '20px',
                  paddingTop: '10px',
                  marginRight: { xxs: '0px', xs: '16px' },
                  width: { xxs: '100%', sm: '350px' },
                }}
              >
                <BoardSkeleton />
              </Box>
            ))
          : displayed.map((company) => (
              <Box
                key={`expertise-${company.id}`}
                sx={{
                  marginBottom: '20px',
                  paddingTop: '10px',
                  marginRight: '16px',
                  width: { xxs: '280px', xs: '350px' },
                }}
              >
                <BoardCard
                  board={company}
                  hideMemberSince
                  companyList
                  width={isXs ? '280px' : '350px'}
                  onClick={() => handleCardClick(company)}
                  displayPillsFlag
                  staticTile
                />
              </Box>
            ))}
      </Box>

      {!hideScroll && displayed.length > 1 && (
        <IconButton
          onClick={scrollRight}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            right: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* {showMoreButton && (
        <Box sx={{ textAlign: 'center', mt: '0px' }}>
          <Button
            variant="text"
            onClick={() => setExpanded(!expanded)}
            sx={{
              border: '1px solid #2C71F64D',
              borderRadius: '8px',
              color: '#2C71F6',
              backgroundColor: '#fff',
              p: '4px 16px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              width: '100%',
              height: '40px',
              '&:hover': { backgroundColor: '#F3F4F6' },
            }}
          >
            {expanded ? 'Less' : 'More'}
          </Button>
        </Box>
      )} */}
    </Paper>
  );
};

export default ExpertiseBoards;
